import { isEqual } from 'lodash';
import { memo, useCallback, useMemo } from 'react';

import { type PublicBoard } from '@air/api/types';

import { BoardGalleryCard } from '~/components/Gallery/BoardGalleryCard/BoardGalleryCard';
import { type GalleryViewRender } from '~/components/Gallery/types';
import { useGetPublicBoardMenuOptionsCallback } from '~/components/PublicBoard/useGetPublicBoardMenuOptionsCallback';
import { PublicGalleryBoardAncestors } from '~/components/PublicGallery/PublicGalleryBoardAncestors';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useOpenPublicBoardInNewTab } from '~/hooks/useOpenPublicBoardInNewTab';
import { usePublicSelectionMenuOptions } from '~/hooks/usePublicSelectionMenuOptions';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { canChangeBoardCustomFields, canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

type PublicBoardGalleryBoardProps = GalleryViewRender<PublicBoard>;

export const PublicBoardGalleryBoard = memo(({ data: board, box }: PublicBoardGalleryBoardProps) => {
  const { isSearchActive } = useIsSearchActive();
  const ancestors = useMemo(
    () =>
      isSearchActive && !!board.ancestors?.length ? (
        <PublicGalleryBoardAncestors ancestors={board.ancestors} />
      ) : undefined,
    [isSearchActive, board.ancestors],
  );
  const { permissions } = usePublicPermissions();
  const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
  const { getBoardMenuOptionsCallback } = useGetPublicBoardMenuOptionsCallback();
  const _getItemMenuOptions = getBoardMenuOptionsCallback({ item: board });
  const { onNavigateToBoard } = usePublicBoard();
  const { openPublicBoardInNewTab } = useOpenPublicBoardInNewTab();
  const onItemClick = useCallback(
    () => onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
    [board, onNavigateToBoard],
  );
  const onItemCmdClick = useCallback(() => openPublicBoardInNewTab({ board }), [board, openPublicBoardInNewTab]);

  const canDownload = canGenerateZip(permissions);
  const canEditCustomFields = canChangeBoardCustomFields(permissions);
  const canViewCustomFields = canSeeBoardCustomFields(permissions);
  const isSelectable = canDownload || canEditCustomFields;

  return (
    <BoardGalleryCard
      board={board}
      cardWidth={box.width}
      getItemMenuOptions={_getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      isSelectable={isSelectable}
      onClick={onItemClick}
      onCmdClickTitle={onItemCmdClick}
      ancestors={ancestors}
      canViewCustomFields={canViewCustomFields}
    />
  );
}, isEqual);

PublicBoardGalleryBoard.displayName = 'PublicBoardGalleryBoard';
